import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Zap, X } from "lucide-react";

/**
 * Shows a banner when the current user is at >=80% of any daily action limit.
 * Dismissal persists for 12 hours in localStorage.
 */
export default function UsageUpsellBanner() {
  const [usage, setUsage] = useState(null);
  const [hidden, setHidden] = useState(() => {
    const until = Number(localStorage.getItem("dara_upsell_snooze") || 0);
    return Date.now() < until;
  });
  const [shownFor, setShownFor] = useState(null);

  useEffect(() => {
    if (hidden) return;
    let active = true;
    api.get("/usage").then(r => { if (active) setUsage(r.data); }).catch(() => {});
    return () => { active = false; };
  }, [hidden]);

  const actions = [
    { key: "insights_generate", label: "insight generations" },
    { key: "assistant_chat", label: "assistant messages" },
  ];
  const triggered = (hidden || !usage) ? null : actions
    .map(a => ({ ...a, ...usage[a.key] }))
    .find(a => a.limit > 0 && a.used / a.limit >= 0.8);

  // log "shown" once per unique trigger key (effect-based to avoid setState-during-render)
  useEffect(() => {
    if (!triggered) return;
    if (shownFor === triggered.key) return;
    setShownFor(triggered.key);
    api.post("/usage/upsell-click", {
      action: "shown",
      surface: "banner",
      triggered_by: triggered.key,
      used: triggered.used,
      limit: triggered.limit,
    }).catch(() => {});
  }, [triggered?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!triggered) return null;
  const pct = Math.round((triggered.used / triggered.limit) * 100);

  const trackUpgrade = () => {
    api.post("/usage/upsell-click", {
      action: "upgrade_clicked",
      surface: "banner",
      triggered_by: triggered.key,
      used: triggered.used,
      limit: triggered.limit,
    }).catch(() => {});
  };

  const snooze = () => {
    api.post("/usage/upsell-click", {
      action: "snoozed",
      surface: "banner",
      triggered_by: triggered.key,
    }).catch(() => {});
    localStorage.setItem("dara_upsell_snooze", String(Date.now() + 12 * 60 * 60 * 1000));
    setHidden(true);
  };

  return (
    <div
      data-testid="upsell-banner"
      className="panel flex items-center justify-between gap-4 px-4 py-3 border-l-2 border-l-[#FFB000]"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 border border-[#FFB000]/40 bg-[#FFB000]/10 flex items-center justify-center flex-shrink-0">
          <Zap className="w-4 h-4 text-[#FFB000]" />
        </div>
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">
            AT {pct}% OF DAILY LIMIT ({triggered.used} / {triggered.limit} {triggered.label})
          </div>
          <div className="text-sm text-zinc-300 mt-0.5 truncate">
            You&apos;re approaching your cap. Upgrade to <span className="text-white font-bold">Enterprise</span> for unlimited.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/app/settings?tab=billing"
          onClick={trackUpgrade}
          data-testid="upsell-upgrade-btn"
          className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:bg-[#E59E00]"
        >
          Upgrade
        </Link>
        <button
          onClick={snooze}
          data-testid="upsell-snooze-btn"
          className="text-zinc-500 hover:text-white p-1"
          title="Hide for 12 hours"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
